// Maps backend field DTOs to frontend SectionConfig with fields, validation, and repeaters
import type { SectionConfig } from '../types/sections';
import type { FieldConfig, FieldType, FieldOption, RepeaterFieldConfig } from '../types/fields';
import type { FieldDTO, SelectOption } from '@startus/lib';
import { resolveOptionsSource } from '@startus/lib';
import { z } from 'zod';

// Resolve locale to supported language key
type Lang = 'en' | 'es' | 'pt';
function resolveLang(locale: string): Lang {
  if (locale.startsWith('pt')) return 'pt';
  return locale === 'es' ? 'es' : 'en';
}

// Resolve localized label with fallback chain: lang → en → es
function resolveLabel(
  map: Partial<Record<Lang, { label: string; placeholder?: string }>> | null | undefined,
  lang: Lang
): { label: string; placeholder?: string } {
  const resolved = map?.[lang] ?? map?.en ?? map?.es;
  return resolved ?? { label: '' };
}

// Map backend fieldType string to frontend FieldType
function mapFieldType(backendType: string): FieldType {
  const typeMap: Record<string, FieldType> = {
    TextField: 'text',
    TextAreaField: 'textarea',
    RadioField: 'radio',
    SelectField: 'select',
    MultiselectField: 'multiselect',
    MultiselectDropdownField: 'multiselectDropdown',
    FileField: 'file',
    ImageField: 'image',
    VideoField: 'video',
    DateField: 'date',
    RepeaterField: 'repeater',
  };
  return typeMap[backendType] || 'text';
}

// Map backend options to FieldOption[] (handles new {label,value} format)
function mapFieldOptions(
  field: FieldDTO,
  lang: Lang,
  countries?: SelectOption[]
): FieldOption[] | undefined {
  if (field.optionsSource === 'countries') {
    return countries ?? resolveOptionsSource('countries') ?? [];
  }
  if (field.optionsSource === 'cities') {
    return []; // resolved dynamically in SectionAccordionItem via fetchCities
  }
  if (field.optionsSource) {
    return resolveOptionsSource(field.optionsSource) ?? [];
  }

  if (!field.options) return undefined;

  const localized = field.options[lang] || field.options.en;
  return localized.map((opt: { label: string; value: string }) => ({
    label: opt.label,
    value: opt.value,
  }));
}

// All fields are optional — BE required/isOptional flags are ignored intentionally
function isFieldRequired(_field: FieldDTO): boolean {
  return false;
}

// Validation error messages per locale — avoids hooks in pure functions
const VALIDATION_MESSAGES = {
  en: {
    required: 'Required',
    invalidFormat: 'Invalid format',
    maxLength: (n: number) => `Maximum ${n} characters`,
    minLength: (n: number) => `Minimum ${n} characters`,
  },
  es: {
    required: 'Requerido',
    invalidFormat: 'Formato inválido',
    maxLength: (n: number) => `Máximo ${n} caracteres`,
    minLength: (n: number) => `Mínimo ${n} caracteres`,
  },
  pt: {
    required: 'Obrigatório',
    invalidFormat: 'Formato inválido',
    maxLength: (n: number) => `Máximo ${n} caracteres`,
    minLength: (n: number) => `Mínimo ${n} caracteres`,
  },
};

// Build Zod schema for a single field using backend validation rules
function buildFieldSchema(field: FieldDTO, lang: Lang = 'en'): z.ZodTypeAny {
  const fieldType = mapFieldType(field.fieldType);
  const v = field.validation;
  const required = isFieldRequired(field);
  const msgs = VALIDATION_MESSAGES[lang];

  // Multiselect / MultiselectDropdown — array of strings
  if (fieldType === 'multiselect' || fieldType === 'multiselectDropdown') {
    let schema = z.array(z.string());
    // maxLength = max number of selections
    if (v?.maxLength) schema = schema.max(v.maxLength);
    if (v?.minLength) schema = schema.min(v.minLength);
    return required ? schema.min(1) : schema.optional();
  }

  // Repeater — array of objects built from itemFields
  if (fieldType === 'repeater' && field.itemFields.length > 0) {
    const itemShape: Record<string, z.ZodTypeAny> = {};
    field.itemFields.forEach((child: FieldDTO) => {
      itemShape[child.id] = buildFieldSchema(child, lang);
    });
    let schema = z.array(z.object(itemShape));
    if (v?.maxLength) schema = schema.max(v.maxLength);
    if (v?.minLength) schema = schema.min(v.minLength);
    return required ? schema : schema.optional();
  }

  // File/Image/Video — permissive schema (file validation handled in component)
  if (fieldType === 'file' || fieldType === 'image' || fieldType === 'video') {
    return required ? z.any() : z.any().optional();
  }

  // Text-based fields (text, textarea, select, radio, date, etc.)
  let schema = z.string();

  if (required) {
    schema = schema.min(1, msgs.required);
  }
  if (v?.maxLength) {
    schema = schema.max(v.maxLength, v?.customMessage?.[lang] || msgs.maxLength(v.maxLength));
  }
  if (v?.minLength && !required) {
    // Only add minLength if not already enforced by required .min(1)
    schema = schema.min(v.minLength, v?.customMessage?.[lang] || msgs.minLength(v.minLength));
  }
  if (v?.pattern) {
    schema = schema.regex(new RegExp(v.pattern), v?.customMessage?.[lang] || msgs.invalidFormat);
  }

  return required ? schema : schema.optional().or(z.literal(''));
}

// Map a repeater child FieldDTO to RepeaterFieldConfig
function mapRepeaterChild(
  child: FieldDTO,
  lang: Lang,
  countries?: SelectOption[]
): RepeaterFieldConfig {
  const childTitle = resolveLabel(child.title, lang);
  return {
    name: child.id, // child UUID as object key in repeater items
    fieldName: child.fieldName, // stable semantic slug (e.g. "founder__first_name")
    type: mapFieldType(child.fieldType) as RepeaterFieldConfig['type'],
    label: childTitle.label,
    placeholder: childTitle.placeholder,
    required: isFieldRequired(child),
    options: mapFieldOptions(child, lang, countries),
    helpText: resolveLabel(child.description, lang).label || undefined,
    maxLength: child.validation?.maxLength,
  };
}

// Map single field DTO to FieldConfig
function mapField(dto: FieldDTO, lang: Lang, countries?: SelectOption[]): FieldConfig {
  const fieldType = mapFieldType(dto.fieldType);
  const fieldTitle = resolveLabel(dto.title, lang);

  const config: FieldConfig = {
    name: dto.id, // UUID as form field name (maps to profileFieldId on save)
    fieldName: dto.fieldName, // stable semantic slug (e.g. "startup_name")
    type: fieldType,
    label: fieldTitle.label,
    placeholder: fieldTitle.placeholder,
    helpText: resolveLabel(dto.description, lang).label || undefined,
    required: isFieldRequired(dto),
    options: mapFieldOptions(dto, lang, countries),
    id: dto.id,
    aiEnabled: dto.aiInteraction === 'REFORMULABLE',
    maxLength: dto.validation?.maxLength,
    optionsSource: dto.optionsSource,
    accept: dto.validation?.accept,
    maxFiles: dto.validation?.maxFiles,
    // Map backend requirements to camelCase
    requirements: dto.requirements?.length
      ? dto.requirements.map((r) => ({ questionId: r.question_id, optionValue: r.option_value }))
      : undefined,
  };

  // Repeater-specific: map embedded child fields
  if (fieldType === 'repeater' && dto.itemFields.length > 0) {
    config.itemFields = dto.itemFields
      .sort((a: FieldDTO, b: FieldDTO) => a.index - b.index)
      .map((child: FieldDTO) => mapRepeaterChild(child, lang, countries));
  }

  return config;
}

// Group fields by sectionId and build complete SectionConfig[]
export function mapProfileFields(
  fields: FieldDTO[],
  locale: string,
  countries?: SelectOption[]
): SectionConfig[] {
  const lang = resolveLang(locale);

  // Filter out child fields and hidden fields
  const topLevelFields = fields.filter((f) => f.parentFieldId === null && f.isVisible !== false);

  // Group by sectionId preserving first-appearance order
  const fieldsBySection = new Map<string, FieldDTO[]>();
  const sectionMeta = new Map<string, FieldDTO['section']>();
  const sectionOrder: string[] = [];

  topLevelFields.forEach((field) => {
    if (!fieldsBySection.has(field.sectionId)) {
      fieldsBySection.set(field.sectionId, []);
      sectionMeta.set(field.sectionId, field.section);
      sectionOrder.push(field.sectionId);
    }
    fieldsBySection.get(field.sectionId)!.push(field);
  });

  // Build SectionConfig for each section
  return sectionOrder.map((sectionId) => {
    const sectionFields = fieldsBySection.get(sectionId)!;
    const metadata = sectionMeta.get(sectionId)!;

    const sortedFields = sectionFields.sort((a, b) => a.index - b.index);

    // Build validation schema shape from all fields in section
    const schemaShape: Record<string, z.ZodTypeAny> = {};
    sortedFields.forEach((field) => {
      schemaShape[field.id] = buildFieldSchema(field, lang);
    });

    return {
      id: sectionId,
      titleKey: resolveLabel(metadata.title, lang).label,
      descriptionKey: undefined,
      fields: sortedFields.map((f) => mapField(f, lang, countries)),
      validationSchema: z.object(schemaShape),
    };
  });
}
